import React, { useState, useEffect } from 'react';
import { Text, Button, Input, Divider, Tooltip, makeStyles, tokens } from '@fluentui/react-components';
import { Navigation24Regular, Dismiss24Regular, ArrowDownload24Regular, ArrowImport24Regular, ArrowUpload24Regular, DataTrending24Regular, Notebook24Regular, People24Regular, QuestionCircle24Regular } from '@fluentui/react-icons';
import { UserProfileMenu } from './UserProfileMenu';
import { AccountSwitcher } from './AccountSwitcher';
import { ManageUsersDialog } from './ManageUsersDialog';
import { HelpPanel } from './HelpPanel';
import { Account } from '../types';
import { useIsMobile } from '../hooks/useIsMobile';
import { ImportCsvDialog } from './ImportCsvDialog';
import { api } from '../services/api';

interface HeaderProps {
  accounts: Account[];
  selectedAccountId: number | null;
  onSelectAccount: (id: number) => void;
  onCreateAccount: (name: string) => void;
  onRenameAccount: (id: number, name: string) => void;
  currentBalance: number;
  startingBalance: number;
  onUpdateStartingBalance: (bal: number) => void;
  onImported?: (rows: any[]) => void;
  view: 'register' | 'insights';
  onToggleView: () => void;
  isAdmin?: boolean;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    borderTopLeftRadius: tokens.borderRadiusLarge,
    borderTopRightRadius: tokens.borderRadiusLarge,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    position: 'sticky',
    top: 0,
    zIndex: 30,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalL,
  },
  balanceSection: {
    display: 'flex',
    alignItems: 'center',
  },
  balancePositive: {
    color: tokens.colorNeutralForegroundOnBrand,
  },
  balanceNegative: {
    color: '#ff6b6b',
  },
  // Mobile header
  mobileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    borderTopLeftRadius: tokens.borderRadiusLarge,
    borderTopRightRadius: tokens.borderRadiusLarge,
    position: 'sticky',
    top: 0,
    zIndex: 30,
  },
  mobileTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  mobileBalance: {
    textAlign: 'right',
  },
  // Drawer overlay
  drawerOverlay: {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 1000,
  },
  drawerPanel: {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '280px',
    maxHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    zIndex: 1001,
    overflowY: 'auto',
    boxShadow: '4px 4px 20px rgba(0,0,0,0.2)',
    borderBottomRightRadius: tokens.borderRadiusXLarge,
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
  },
  drawerSection: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
  },
  drawerSectionTitle: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalS,
  },
  drawerMenuItem: {
    width: '100%',
    justifyContent: 'flex-start',
    marginBottom: tokens.spacingVerticalXS,
  },
});

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export const Header: React.FC<HeaderProps> = ({
  accounts, selectedAccountId, onSelectAccount, onCreateAccount, onRenameAccount, currentBalance, startingBalance, onUpdateStartingBalance, onImported, view, onToggleView, isAdmin,
}) => {
  const styles = useStyles();
  const isMobile = useIsMobile();
  const isNegative = currentBalance < 0;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [startBalValue, setStartBalValue] = useState(String(startingBalance));

  useEffect(() => {
    setStartBalValue(String(startingBalance));
  }, [startingBalance, selectedAccountId]);

  const handleSetStartingBalance = () => {
    const num = parseFloat(startBalValue);
    if (!isNaN(num)) onUpdateStartingBalance(num);
  };

  if (isMobile) {
    return (
      <>
        <div className={styles.mobileHeader}>
          <div className={styles.mobileTitle}>
            <Button
              appearance="transparent"
              icon={<Navigation24Regular />}
              onClick={() => setDrawerOpen(true)}
              style={{ color: 'inherit', minWidth: '36px' }}
            />
            <Text as="h1" size={500} weight="semibold"
              style={{ color: 'inherit', margin: 0, cursor: 'pointer' }}
              onClick={() => { if (view !== 'register') onToggleView(); setDrawerOpen(false); }}
            >
              BalanceScript
            </Text>
          </div>
          <div className={styles.mobileBalance}>
            <Text size={400} weight="bold" style={{ color: isNegative ? '#ff6b6b' : 'inherit' }}>
              {formatMoney(currentBalance)}
            </Text>
          </div>
        </div>

        {/* Mobile drawer */}
        {drawerOpen && (
          <>
            <div className={styles.drawerOverlay} onClick={() => setDrawerOpen(false)} />
            <div className={styles.drawerPanel}>
              <div className={styles.drawerHeader}>
                <Text size={500} weight="semibold" style={{ color: 'inherit' }}>BalanceScript</Text>
                <Button appearance="transparent" icon={<Dismiss24Regular />}
                  onClick={() => setDrawerOpen(false)} style={{ color: 'inherit' }} />
              </div>

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Account</div>
                <AccountSwitcher
                  accounts={accounts}
                  selectedAccountId={selectedAccountId}
                  onSelect={(id) => { onSelectAccount(id); setDrawerOpen(false); }}
                  onCreate={onCreateAccount}
                  onRename={onRenameAccount}
                />
              </div>

              <Divider />

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Balance</div>
                <Text size={600} weight="bold" style={{ color: isNegative ? '#c0392b' : '#27ae60' }}>
                  {formatMoney(currentBalance)}
                </Text>
              </div>

              <Divider />

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Views</div>
                <Button
                  className={styles.drawerMenuItem}
                  appearance={view === 'register' ? 'primary' : 'subtle'}
                  icon={<Notebook24Regular />}
                  onClick={() => { if (view !== 'register') onToggleView(); setDrawerOpen(false); }}
                >
                  Register
                </Button>
                <Button
                  className={styles.drawerMenuItem}
                  appearance={view === 'insights' ? 'primary' : 'subtle'}
                  icon={<DataTrending24Regular />}
                  onClick={() => { if (view !== 'insights') onToggleView(); setDrawerOpen(false); }}
                >
                  Insights
                </Button>
              </div>

              <Divider />

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Tools</div>
                <Button
                  className={styles.drawerMenuItem}
                  appearance="subtle"
                  icon={<ArrowUpload24Regular />}
                  onClick={() => {
                    if (selectedAccountId) {
                      const acct = accounts.find(a => a.id === selectedAccountId);
                      api.exportTransactions(selectedAccountId, acct?.name || 'account');
                    }
                    setDrawerOpen(false);
                  }}
                  disabled={!selectedAccountId}
                >
                  Export CSV
                </Button>
                <Button
                  className={styles.drawerMenuItem}
                  appearance="subtle"
                  icon={<ArrowDownload24Regular />}
                  onClick={() => {
                    if (selectedAccountId) api.downloadTemplate(selectedAccountId);
                    setDrawerOpen(false);
                  }}
                  disabled={!selectedAccountId}
                >
                  Download Template
                </Button>
                <Button
                  className={styles.drawerMenuItem}
                  appearance="subtle"
                  icon={<ArrowImport24Regular />}
                  onClick={() => { setImportOpen(true); setDrawerOpen(false); }}
                  disabled={!selectedAccountId}
                >
                  Import CSV
                </Button>
                {isAdmin && (
                  <Button
                    className={styles.drawerMenuItem}
                    appearance="subtle"
                    icon={<People24Regular />}
                    onClick={() => { setUsersOpen(true); setDrawerOpen(false); }}
                  >
                    Manage Users
                  </Button>
                )}
              </div>

              <Divider />

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Help</div>
                <Button
                  className={styles.drawerMenuItem}
                  appearance="subtle"
                  icon={<QuestionCircle24Regular />}
                  onClick={() => { setHelpOpen(true); setDrawerOpen(false); }}
                >
                  Help & Guide
                </Button>
              </div>

              <Divider />

              <div className={styles.drawerSection}>
                <div className={styles.drawerSectionTitle}>Profile</div>
                <UserProfileMenu />
              </div>
            </div>
          </>
        )}

        {selectedAccountId && onImported && (
          <ImportCsvDialog
            accountId={selectedAccountId}
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onImported={onImported}
          />
        )}
        {isAdmin && (
          <ManageUsersDialog
            open={usersOpen}
            onClose={() => setUsersOpen(false)}
          />
        )}
        <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      </>
    );
  }

  // Desktop header (unchanged)
  return (
    <div className={styles.header}>
      <div className={styles.left}>
        <Text as="h1" size={600} weight="semibold"
          style={{ color: 'inherit', margin: 0, cursor: 'pointer' }}
          onClick={() => { if (view !== 'register') onToggleView(); }}
        >
          BalanceScript
        </Text>
        <AccountSwitcher
          accounts={accounts}
          selectedAccountId={selectedAccountId}
          onSelect={onSelectAccount}
          onCreate={onCreateAccount}
          onRename={onRenameAccount}
        />
        <Tooltip content="Your account's starting balance. All transactions calculate from this amount." relationship="description">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
            <Text size={200} style={{ color: 'inherit', opacity: 0.8 }}>Start: $</Text>
            <Input
              type="number"
              value={startBalValue}
              onChange={(e, d) => setStartBalValue(d.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSetStartingBalance(); }}
              onBlur={handleSetStartingBalance}
              size="small"
              style={{
                width: '110px',
                backgroundColor: '#fff',
                color: '#242424',
                borderRadius: '4px',
              }}
            />
          </div>
        </Tooltip>
      </div>
      <div className={styles.right}>
        <Tooltip content="View spending breakdowns, recurring costs, and charts" relationship="label">
          <Button
            icon={view === 'register' ? <DataTrending24Regular /> : <Notebook24Regular />}
            appearance="transparent"
            onClick={onToggleView}
            style={{ color: 'inherit' }}
            size="small"
          >
            {view === 'register' ? 'Insights' : 'Register'}
          </Button>
        </Tooltip>
        <Tooltip content="Your current running balance across all transactions" relationship="label">
          <div className={styles.balanceSection}>
            <Text size={200} style={{ color: 'inherit', opacity: 0.8, marginRight: '6px' }}>
              Balance:
            </Text>
            <Text size={500} weight="bold" className={isNegative ? styles.balanceNegative : styles.balancePositive}>
              {formatMoney(currentBalance)}
            </Text>
          </div>
        </Tooltip>
        <Tooltip content="Help and user guide" relationship="label">
          <Button
            icon={<QuestionCircle24Regular />}
            appearance="transparent"
            onClick={() => setHelpOpen(true)}
            style={{ color: 'inherit' }}
            size="small"
          />
        </Tooltip>
        <UserProfileMenu
          accountId={selectedAccountId}
          accountName={accounts.find(a => a.id === selectedAccountId)?.name}
          isOwner={(() => { const acct = accounts.find(a => a.id === selectedAccountId); return !acct?.access_type || acct?.access_type === 'owner'; })()}
          isAdmin={isAdmin}
          onImported={onImported}
        />
      </div>
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};
